import { ENV_CONFIG } from '../../../config/environment';
import {
  FEATURE_DESCRIPTIONS,
  PREMIUM_FAMILY_MEMBER_LIMIT,
  PremiumFeature,
  SubscriptionPlan,
  SubscriptionTier,
} from '../types/premiumFeatures';

const LEGACY_PLAN_ALIASES = ['individual', 'family', 'group_small', 'group_medium', 'group_large'];

const normalizePlanId = (planId: string): string => {
  return planId === 'premium' || LEGACY_PLAN_ALIASES.includes(planId) ? 'premium' : planId;
};

export const createSubscriptionPlans = (): SubscriptionPlan[] => [
  {
    id: 'premium',
    name: 'Premium',
    description: `Unlimited Bible quiz for you and your household, including up to ${PREMIUM_FAMILY_MEMBER_LIMIT} invited family members.`,
    tier: SubscriptionTier.PREMIUM,
    price: {
      monthly: parseFloat(ENV_CONFIG.SUBSCRIPTION_TIERS.FAMILY.MONTHLY),
      annual: parseFloat(ENV_CONFIG.SUBSCRIPTION_TIERS.FAMILY.ANNUAL),
    },
    features: [
      PremiumFeature.UNLIMITED_DAILY_QUIZZES,
      PremiumFeature.AD_FREE_EXPERIENCE,
      PremiumFeature.UNLIMITED_QUIZ_HISTORY,
      PremiumFeature.MULTIPLE_QUESTION_FORMATS,
      PremiumFeature.MULTI_LANGUAGE_SUPPORT,
      PremiumFeature.MULTIPLAYER_CREATE,
      PremiumFeature.MULTIPLAYER_JOIN,
      PremiumFeature.GAME_ROOM_CREATE,
      PremiumFeature.GAME_ROOM_JOIN,
      PremiumFeature.FAMILY_ANALYTICS,
      PremiumFeature.TEAM_MANAGEMENT,
      PremiumFeature.PREMIUM_QUIZ_PACKS,
    ],
    limits: {
      quizHistory: -1,
      familyMembers: PREMIUM_FAMILY_MEMBER_LIMIT,
      concurrentMultiplayerRooms: 3,
      concurrentGameRooms: 2,
      storageGB: 5,
    },
    stripePriceIds: {
      monthly: ENV_CONFIG.SUBSCRIPTION_TIERS.FAMILY.STRIPE_MONTHLY_PRICE_ID,
      annual: ENV_CONFIG.SUBSCRIPTION_TIERS.FAMILY.STRIPE_ANNUAL_PRICE_ID,
    },
    popular: true,
    legacyAliases: LEGACY_PLAN_ALIASES,
  },
];

export const getSubscriptionPlans = (): SubscriptionPlan[] => createSubscriptionPlans();

export const getPersonalPlans = (): SubscriptionPlan[] => getSubscriptionPlans();

export const getGroupPlans = (): SubscriptionPlan[] => [];

export const getSubscriptionPlan = (planId: string): SubscriptionPlan | null => {
  const normalizedPlanId = normalizePlanId(planId);
  return getSubscriptionPlans().find((plan) => plan.id === normalizedPlanId) || null;
};

export const getSubscriptionPlanByTier = (tier: SubscriptionTier): SubscriptionPlan | null => {
  return tier === SubscriptionTier.FREE ? null : getSubscriptionPlans()[0] || null;
};

export const getAnnualSavingsPercentage = (plan: SubscriptionPlan): number => {
  const monthlyTotal = plan.price.monthly * 12;
  const savings = monthlyTotal - plan.price.annual;
  return Math.round((savings / monthlyTotal) * 100);
};

export const getFeatureComparison = (plans: SubscriptionPlan[]): {
  feature: PremiumFeature;
  description: string;
  plans: { [planId: string]: boolean };
}[] => {
  const allFeatures = new Set<PremiumFeature>();

  plans.forEach((plan) => {
    plan.features.forEach((feature) => allFeatures.add(feature));
  });

  return Array.from(allFeatures).map((feature) => ({
    feature,
    description: FEATURE_DESCRIPTIONS[feature],
    plans: plans.reduce<{ [planId: string]: boolean }>((acc, plan) => {
      acc[plan.id] = plan.features.includes(feature);
      return acc;
    }, {}),
  }));
};

export const validateSubscriptionPlans = (): { valid: boolean; errors: string[] } => {
  const plans = getSubscriptionPlans();
  const errors: string[] = [];

  plans.forEach((plan) => {
    if (!plan.id || !plan.name || !plan.description) {
      errors.push(`Plan ${plan.id || 'unknown'} missing required fields`);
    }

    if (plan.price.monthly <= 0 || plan.price.annual <= 0) {
      errors.push(`Plan ${plan.id} has invalid pricing`);
    }

    if (plan.price.annual >= plan.price.monthly * 12) {
      errors.push(`Plan ${plan.id} annual price should be less than monthly * 12`);
    }

    if (ENV_CONFIG.MONETIZATION.SUBSCRIPTION_ENABLED) {
      if (!plan.stripePriceIds.monthly || !plan.stripePriceIds.annual) {
        errors.push(`Plan ${plan.id} missing Stripe price IDs`);
      }
    }
  });

  return {
    valid: errors.length === 0,
    errors,
  };
};

export default {
  getSubscriptionPlans,
  getPersonalPlans,
  getGroupPlans,
  getSubscriptionPlan,
  getSubscriptionPlanByTier,
  getAnnualSavingsPercentage,
  getFeatureComparison,
  validateSubscriptionPlans,
};
